import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  DEFAULT_ROLE_PERMISSIONS,
  type PermissionKey,
  type Role,
  type UserRole,
} from '@afiliados/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { RoleBadge } from '@/components/RoleBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { authApi, rolesApi, type PermissionInfo } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { useAuthStore, useCan } from '@/features/auth/store';

type Draft = Partial<Record<UserRole, PermissionKey[]>>;

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((k) => b.includes(k));

export function RolesPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const canManage = useCan('roles.manage');
  const roles = useQuery({ queryKey: ['roles'], queryFn: rolesApi.list });
  const permissions = useQuery({
    queryKey: ['roles', 'permissions'],
    queryFn: rolesApi.permissions,
  });
  const [draft, setDraft] = useState<Draft>({});

  useEffect(() => {
    if (roles.data) setDraft(Object.fromEntries(roles.data.map((r) => [r.key, r.permissions])));
  }, [roles.data]);

  const groups = useMemo(() => {
    const map = new Map<string, PermissionInfo[]>();
    for (const p of permissions.data ?? []) {
      if (!map.has(p.feature)) map.set(p.feature, []);
      map.get(p.feature)!.push(p);
    }
    return [...map.entries()];
  }, [permissions.data]);

  const save = useMutation({
    mutationFn: (role: UserRole) => rolesApi.update(role, draft[role] ?? []),
    onSuccess: async (updated) => {
      toast.success(`Permissões de ${updated.name} salvas`);
      await qc.invalidateQueries({ queryKey: ['roles'] });
      // Mudou o próprio perfil: atualiza o menu e as ações na hora.
      if (me?.role === updated.key) setUser(await authApi.me());
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao salvar'),
  });

  const toggle = (role: UserRole, key: PermissionKey) =>
    setDraft((d) => {
      const cur = d[role] ?? [];
      return { ...d, [role]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
    });

  const isDirty = (r: Role) => !sameSet(draft[r.key] ?? [], r.permissions);

  if (roles.isLoading || permissions.isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-96" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Perfis e permissões"
        description={
          canManage
            ? 'Marque o que cada perfil pode fazer. As mudanças valem na hora para quem já está logado.'
            : 'O que cada perfil pode fazer. Só administradores alteram.'
        }
      />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {roles.data?.map((role) => {
          const current = draft[role.key] ?? [];
          const has = (k: PermissionKey) => role.fixed || current.includes(k);
          const editable = canManage && !role.locked;
          return (
            <Card key={role.key} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      {role.name}
                      {role.locked && <Lock className="size-3.5 text-muted-foreground" />}
                    </CardTitle>
                    <CardDescription className="mt-1">{role.description}</CardDescription>
                  </div>
                  <RoleBadge role={role.key} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {role.userCount} usuário(s) ·{' '}
                  {role.fixed ? 'todas as permissões' : `${current.length} permissão(ões)`}
                </p>
                {canManage && role.locked && !role.fixed && (
                  <p className="text-xs text-muted-foreground">
                    Só o Super Administrador altera este perfil.
                  </p>
                )}
              </CardHeader>
              <CardContent className="flex flex-1 flex-col gap-4">
                {groups.map(([feature, list]) => (
                  <div key={feature}>
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {feature}
                    </p>
                    <div className="space-y-1">
                      {list.map((p) => {
                        const id = `${role.key}-${p.key}`;
                        return (
                          <label
                            key={p.key}
                            htmlFor={id}
                            className={`flex items-start gap-2.5 rounded-md px-2 py-1.5 text-sm ${
                              editable ? 'cursor-pointer hover:bg-muted/60' : ''
                            } ${has(p.key) ? '' : 'text-muted-foreground'}`}
                          >
                            <Checkbox
                              id={id}
                              checked={has(p.key)}
                              disabled={!editable}
                              onCheckedChange={() => toggle(role.key, p.key)}
                              className="mt-0.5"
                            />
                            <span>
                              {p.description}
                              <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                                {p.key}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {editable && (
                  <div className="mt-auto flex gap-2 border-t pt-4">
                    <Button
                      className="flex-1"
                      size="sm"
                      disabled={!isDirty(role) || save.isPending}
                      onClick={() => save.mutate(role.key)}
                    >
                      {save.isPending && save.variables === role.key ? (
                        <Loader2 className="animate-spin" />
                      ) : (
                        <Save />
                      )}
                      {isDirty(role) ? 'Salvar alterações' : 'Sem alterações'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      title="Voltar ao padrão do sistema"
                      disabled={sameSet(current, DEFAULT_ROLE_PERMISSIONS[role.key])}
                      onClick={() =>
                        setDraft((d) => ({
                          ...d,
                          [role.key]: [...DEFAULT_ROLE_PERMISSIONS[role.key]],
                        }))
                      }
                    >
                      <RotateCcw /> Padrão
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
